// EL MÓDULO DE NAVEGACIÓN · TC-DR-AT (3 de octubre de 2026).
//
// EL DEFECTO. TEGO cambiaba de pantalla por estado interno sin tocar el
// historial del navegador: toda la sesión era UNA entrada, y «atrás»
// saltaba al documento anterior — el portal. Medido en el teléfono del
// dueño: Mi trabajo → Entrada → atrás = «Hola, somos TEGO».
//
// LA REGLA. Atrás deshace el último paso visible, uno a la vez, y nunca
// saca de TEGO sin avisar. Cada cosa que tapa o cambia la pantalla es una
// entrada del historial, apilada en este orden:
//
//     centinela › Mi trabajo › módulo › subpantalla › paso › capa
//
// así que un atrás baja exactamente una, y la prioridad sale sola.
//
// UNA SOLA PUERTA. Este es el único archivo de `frontend/src` que toca
// `history`, `popstate` y `location.replace`. Todo lo demás pasa por aquí.
//
// EL NÚCLEO es JS puro entre las dos marcas de abajo: sin `import`, sin
// `window` y sin `document`; todo le llega por `entorno`. Así se prueba
// con `node:test` y dobles, y el mismo bloque, byte a byte, sirve a TEGO
// OPS (que no tiene empaquetador). Lo que hay fuera de las marcas es el
// cableado al navegador de verdad.
import { urlDe } from './navegacionContexto';

/* NUCLEO:INICIO */
function crearNavegacion(entorno) {
  var history = entorno.history;
  var location = entorno.location;
  var ahora = entorno.ahora || Date.now;
  var VENTANA_DE_SALIDA = 2000;      // lo que dura el aviso de la raíz
  var ESPERA_DE_TRAVESIA = 1000;     // una travesía que no llega se da por perdida
  var REINTENTOS_DE_SCROLL = 30;     // 30 × 100 ms: lo que tarda una lista en cargar
  var SOSTEN_DE_SCROLL = 5;          // 5 × 100 ms quieta en su sitio: restaurada
  var VENTANA_DE_GESTO = 1500;       // del toque que abre una ficha a que la ficha se pinta

  var s = null;               // id de la pila
  var pos = null;             // la entrada APLICADA (lo que la pantalla muestra)
  var iNav = 0;               // el índice donde está el NAVEGADOR (puede adelantarse a `pos`)
  var iniciado = false;
  var conAviso = false;       // hay sesión: la raíz avisa antes de salir
  var haySesion = false;      // hay sesión aunque todavía no haya raíz (una pantalla previa a la aplicación)
  var raizActual = null;      // { m }
  var avisoEn = -Infinity;
  var avisoVisible = false;
  var relojAviso = null;
  var capasVivas = [];        // { id, i, cerrar, bloqueada, tieneCambios, detalle }
  var registros = [];         // { mb, b, tieneCambios, detalle, sub }
  var manejSubs = {};         // clave → { abrir, cerrar }
  var manejPasos = {};        // clave → { fijar, inicial }
  var pendiente = null;       // { vista: { detalle }, alSalir }
  var cola = [];
  var enVuelo = null;         // { destino, silenciosa, origen, reloj }
  var drenando = false;
  var pantallaActual = null;
  var suscriptores = [];
  var salidas = [];           // antesDeSalir
  var ys = {};                // scroll por entrada, en memoria
  var ultimoY = 0;
  var relojScroll = null;
  var restauracion = null;    // { y }: la restauración de scroll en curso
  var gesto = null;           // { y, en }: el scroll que había al último toque o tecla
  var rearme = false;         // N2: la siembra ocurrió sin gesto del usuario

  // ---- utilidades ---------------------------------------------------
  function copia(base, cambios) {
    var o = {}, k;
    for (k in base) if (Object.prototype.hasOwnProperty.call(base, k)) o[k] = base[k];
    for (k in cambios) if (Object.prototype.hasOwnProperty.call(cambios, k)) o[k] = cambios[k];
    return o;
  }
  function nueva(i, tipo, extra) {
    return copia({ tego: 2, s: s, i: i, tipo: tipo, mb: i, b: i, m: null, ctx: null,
                   subs: [], paso: null, capas: [], y: 0 }, extra || {});
  }
  function normalizar(st) {
    if (!st || st.tego !== 2 || !st.s) return null;
    if (s && st.s !== s) return null;
    return st;
  }
  function urlActual() {
    return location.pathname + (location.search || '') + (location.hash || '');
  }
  function idNuevo() {
    return ahora().toString(36) + Math.random().toString(36).slice(2, 8);
  }
  function ctxVacio(ctx) {
    if (ctx === null || ctx === undefined) return true;
    if (typeof ctx !== 'object') return false;
    for (var k in ctx) if (Object.prototype.hasOwnProperty.call(ctx, k)) return false;
    return true;
  }
  function mismoCtx(a, b) {
    if (ctxVacio(a) && ctxVacio(b)) return true;
    return JSON.stringify(a) === JSON.stringify(b);
  }
  function leerScroll() {
    try { return entorno.leerScroll ? (entorno.leerScroll() || 0) : 0; } catch { return 0; }
  }
  // FIJAR, Y COMPROBAR QUE EL NAVEGADOR OBEDECIÓ · auditoría H9 (3.ª vuelta).
  // WebKit aplica cada desplazamiento pedido como una DIFERENCIA contra la
  // última posición que se le pidió, no contra la real. Si el documento
  // encoge y el navegador recorta el scroll (300 → 158), esa «última
  // pedida» sigue en 300: pedir otra vez 300 es una diferencia de cero y no
  // mueve nada, y pedir 600 deja 458. Medido en WebKit 390×844 (Conteos →
  // «Continuar» → atrás): `scrollTo(0, 300)` desde 158 deja 158; también
  // `scrollBy(0, 142)` y `scrollTop = 300`. Ir a 0 sí los reconcilia (0 es
  // 0 en las dos cuentas), y `scrollTo(0, 0); scrollTo(0, 300)` deja 300.
  // Así que, si cabe y no llegó, se pasa por 0 y se vuelve a pedir, en el
  // mismo instante: entre las dos no hay pintura, no se ve ningún salto.
  function fijarScroll(y) {
    try {
      if (entorno.fijarScroll) {
        entorno.fijarScroll(y);
        if (y > 0 && Math.abs(leerScroll() - y) > 1 && topeDeScroll(0) >= y) {
          entorno.fijarScroll(0);
          entorno.fijarScroll(y);
        }
      }
    } catch { /* sin scroll */ }
    ultimoY = y;
  }
  function temporizador(fn, ms) {
    return entorno.temporizador ? entorno.temporizador(fn, ms) : null;
  }
  function cancelar(id) {
    if (id !== null && id !== undefined && entorno.cancelar) entorno.cancelar(id);
  }
  function gestoPrevio() {
    try { return entorno.gestoPrevio ? entorno.gestoPrevio() : undefined; } catch { return undefined; }
  }
  function moduloDeUrl() {
    var m = /[?&]m=([^&#]*)/.exec(location.search || '');
    if (!m) return null;
    try { return decodeURIComponent(m[1].replace(/\+/g, ' ')) || null; } catch { return null; }
  }
  function valorSub(L, clave) {
    var subs = (L && L.subs) || [];
    for (var k = 0; k < subs.length; k++) if (subs[k].clave === clave) return subs[k];
    return undefined;
  }
  function serializable(v) {
    return typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean';
  }
  function dice(fn) {
    try { return !!(fn && fn()); } catch { return false; }
  }
  function escribir(st, url, empujar) {
    if (empujar) history.pushState(st, '', url);
    else history.replaceState(st, '', url);
    pos = st;
    iNav = st.i;
  }
  function fijarPantalla(L) {
    pantallaActual = { m: L.m, ctx: L.ctx === undefined ? null : L.ctx, clave: L.s + ':' + L.mb };
  }
  function notificar() {
    var lista = suscriptores.slice();
    for (var k = 0; k < lista.length; k++) {
      try { lista[k](); } catch { /* un oyente roto no detiene la navegación */ }
    }
  }
  function asegurar() {
    if (!pos) iniciar({ centinela: entorno.centinelaPorOmision ? !!entorno.centinelaPorOmision() : false });
    return !!pos;
  }

  // ---- la cola serial (lo que hace seguro el «X») -------------------
  // Las travesías (`history.go`) son asíncronas y `pushState` es síncrono:
  // mezclarlos desordena la pila. Toda mutación pasa por aquí, en orden.
  function encolar(op) { cola.push(op); drenar(); }
  function drenar() {
    if (drenando) return;
    drenando = true;
    try {
      while (!enVuelo && cola.length) {
        var op = cola.shift();
        if (typeof op === 'function') { op(); continue; }
        var delta = op.hasta - iNav;
        if (delta === 0) continue;
        enVuelo = { destino: op.hasta, silenciosa: !!op.silenciosa, origen: pos,
                    reloj: temporizador(vencer, ESPERA_DE_TRAVESIA) };
        history.go(delta);
      }
    } finally { drenando = false; }
  }
  // La travesía no llegó: no había a dónde ir. Se cree al navegador.
  function vencer() {
    enVuelo = null;
    var L = normalizar(history.state);
    if (L) { pos = L; iNav = L.i; }
    drenar();
  }

  // ---- scroll --------------------------------------------------------
  function topeDeScroll(porOmision) {
    try { return entorno.topeDeScroll ? entorno.topeDeScroll() : porOmision; } catch { return porOmision; }
  }
  function cancelarRestauracion() {
    if (relojScroll !== null) { cancelar(relojScroll); relojScroll = null; }
    restauracion = null;
  }
  // VOLVER A DONDE SE ESTABA, AUNQUE LA PANTALLA TARDE EN TENER ALTO.
  // Al volver, la pantalla de debajo puede no estar pintada todavía (la
  // ficha sigue en el documento un instante) o pintarse corta mientras
  // carga: el navegador recorta entonces el scroll, y fijarlo una sola vez
  // lo perdía. Se fija en cuanto cabe y se SOSTIENE: si el documento se
  // acorta y el scroll cae, se vuelve a fijar cuando vuelve a caber. Acaba
  // cuando lleva medio segundo quieta, a los tres segundos, o en cuanto el
  // usuario mueve la página con un gesto (rueda, dedo, tecla, o apretar la
  // barra: ver `soltarPorGesto`).
  //
  // SOLO EL GESTO SUELTA, NO EL EVENTO `scroll` · auditoría H9 (2.ª vuelta).
  // Antes, un scroll fuera de su sitio con el documento ya alto se tomaba
  // por la mano del usuario y la restauración se rendía. En WebKit el
  // recorte llega tarde: se fija y=300 sobre la ficha todavía alta, React
  // pinta la lista cargando (corta) y el navegador recorta a su tope (158);
  // los datos llegan, el documento vuelve a medir 2 268, y el evento
  // `scroll` del recorte se entrega DESPUÉS, con el documento ya alto. Nadie
  // vio el documento corto, el 158 pasaba por gesto y la lista se quedaba
  // ahí (Conteos → «Continuar» → atrás). Un `scroll` no dice quién lo
  // causó; un gesto sí. Así que el sostén repone siempre que esté fuera de
  // su sitio y quepa, y solo lo detienen el gesto, la quietud o el tope.
  function restaurarScroll(y) {
    cancelarRestauracion();
    if (!(y > 0)) { fijarScroll(0); return; }
    var intento = 0, quieta = 0;
    var mia = { y: y };
    restauracion = mia;
    function acabar() { if (restauracion === mia) restauracion = null; }
    function probar() {
      relojScroll = null;
      var t = topeDeScroll(y);
      if (t >= y) {
        if (Math.abs(leerScroll() - y) > 1) {
          fijarScroll(y); quieta = 0;               // cabe y no está en su sitio: se repone
        } else {
          quieta += 1;
          if (quieta >= SOSTEN_DE_SCROLL) { acabar(); return; }
        }
      } else {
        quieta = 0;                                 // todavía no cabe: se espera
      }
      if (intento >= REINTENTOS_DE_SCROLL) {
        if (t < y) fijarScroll(Math.max(0, Math.min(y, t)));
        acabar();
        return;
      }
      intento += 1;
      relojScroll = temporizador(probar, 100);
    }
    probar();
  }
  function alScroll() {
    // Con una restauración en curso, el scroll que se mueve es un recorte
    // del navegador o la propia restauración, no la mano del usuario (la
    // mano llega como gesto y suelta antes): no se anota como posición.
    if (!restauracion) ultimoY = leerScroll();
  }
  // El usuario tomó la página: manda él, y su posición es la que se anota.
  function soltarPorGesto() {
    if (!restauracion) return;
    cancelarRestauracion();
    ultimoY = leerScroll();
  }
  // EL SCROLL DE LA PANTALLA QUE SE DEJA AL ABRIR UNA SUBPANTALLA. Quien
  // abre una ficha lo anuncia DESPUÉS de pintarla (el gancho mira el estado
  // de la pantalla), y una ficha recién abierta es corta mientras carga: el
  // navegador ya recortó el scroll de la lista, y se guardaba un cero
  // (Productos → Ficha → atrás dejaba la lista arriba del todo). Si el
  // scroll está recortado contra el fondo del documento y hace un momento,
  // al toque que abrió la ficha, era mayor, vale el del toque.
  function anotarGesto() { gesto = { y: leerScroll(), en: ahora() }; }
  function scrollQueSeDeja() {
    var y = leerScroll();
    if (gesto && gesto.y > y && ahora() - gesto.en <= VENTANA_DE_GESTO && y >= topeDeScroll(y) - 1) return gesto.y;
    return y;
  }
  function scrollGuardado(L) {
    if (ys[L.i] !== undefined) return ys[L.i];
    return L.y || 0;
  }

  // ---- siembra -------------------------------------------------------
  function oir() {
    if (!entorno.escuchar) return;
    entorno.escuchar('popstate', alPopstate);
    entorno.escuchar('pageshow', function (ev) {
      if (!ev || !ev.persisted) return;
      var L = normalizar(history.state);
      if (L) { pos = L; iNav = L.i; }
    });
    entorno.escuchar('pagehide', function () {
      // Para la recarga: el scroll de la entrada donde se está.
      var L = normalizar(history.state);
      if (!L || !pos || L.i !== pos.i) return;
      try { history.replaceState(copia(L, { y: leerScroll() }), '', urlActual()); } catch { /* nada */ }
    });
    entorno.escuchar('scroll', alScroll, { passive: true });
    // Los gestos que sueltan una restauración en curso. `pointerdown` y
    // `mousedown`: arrastrar la barra empieza apretándola.
    entorno.escuchar('wheel', soltarPorGesto, { passive: true });
    entorno.escuchar('touchstart', soltarPorGesto, { passive: true });
    entorno.escuchar('touchmove', soltarPorGesto, { passive: true });
    entorno.escuchar('keydown', soltarPorGesto, { passive: true });
    entorno.escuchar('pointerdown', soltarPorGesto, { passive: true });
    entorno.escuchar('mousedown', soltarPorGesto, { passive: true });
    // N2 · el primer gesto de verdad. `click` y `keydown`, en captura; no
    // `pointerdown`, que en táctil todavía no cuenta como gesto.
    entorno.escuchar('click', rearmar, true);
    entorno.escuchar('keydown', rearmar, true);
    // En captura: el scroll se anota antes de que la pantalla reaccione.
    entorno.escuchar('click', anotarGesto, true);
    entorno.escuchar('keydown', anotarGesto, true);
  }

  function iniciar(op) {
    var centinela = !!(op && op.centinela);
    if (iniciado) return false;
    if (!history || typeof history.pushState !== 'function') return false;
    var previo = history.state;
    var L = previo && previo.tego === 2 && previo.s ? previo : null;
    var sembro = false;
    if (L) {
      // Recarga con la pila presente: se adopta, no se siembra. El
      // centinela y la raíz siguen debajo: el navegador conserva la pila.
      s = L.s; pos = L; iNav = L.i;
    } else {
      // Primera carga (o el state del truco anterior, `{tego:'app'}`, que
      // se trata como nulo y se siembra encima).
      var url = urlActual();
      s = idNuevo();
      var puerta = nueva(1, 'puerta');
      try {
        if (centinela) {
          history.replaceState(nueva(0, 'centinela'), '', url);
          history.pushState(puerta, '', url);
        } else {
          history.replaceState(puerta, '', url);
        }
      } catch {
        s = null;                      // origen raro (file://): no se toca
        return false;
      }
      pos = puerta; iNav = 1; sembro = true;
      rearme = centinela && gestoPrevio() === false;
    }
    iniciado = true;
    try { history.scrollRestoration = 'manual'; } catch { /* navegador viejo */ }
    ultimoY = leerScroll();
    oir();
    // Recarga sobre una capa, un paso o una ficha que no se puede volver a
    // abrir: se baja a la pantalla que la sostiene. La capa no se reabre.
    // Tras recargar no queda nada abierto en memoria, así que no se pregunta
    // a `materializable`: se decide con lo que la entrada dice de sí misma.
    if (L) {
      var hasta = (L.tipo === 'capa' || L.tipo === 'paso') ? L.b : L.i;
      var ultima = L.subs && L.subs[L.subs.length - 1];
      if (ultima && ultima.valor === true) hasta = L.mb;
      if (hasta !== L.i) encolar({ hasta: hasta });
      else if (L.y > 0) restaurarScroll(L.y);
    }
    return sembro;
  }

  function sembrar(op) {
    iniciar({ centinela: true });
    if (!pos || !op || !op.raiz) return;
    conAviso = true;
    raizActual = { m: op.raiz.m };
    // Sin `destino` (la segunda llamada, desde el efecto) no se mueve nada:
    // solo se vuelve a encender el aviso de la raíz.
    var pedido = op.destino && op.destino.m
      ? { m: op.destino.m, ctx: op.destino.ctx === undefined ? null : op.destino.ctx }
      : null;
    var d = pedido || { m: raizActual.m, ctx: null };
    var url = urlActual();
    if (pos.tipo !== 'puerta') {
      // Recarga o reingreso: la pila ya existe. Si lo que se resolvió no
      // es lo que la entrada dice (el rol de quien entra no tiene ese
      // módulo, o la entrada es de antes de entrar), la entrada pasa a
      // decir lo que de verdad se abre.
      if (pedido && pos.m !== d.m) {
        escribir(copia(pos, { tipo: 'modulo', mb: pos.i, b: pos.i, m: d.m, ctx: d.ctx,
                              subs: [], paso: null, capas: [] }),
                 moduloDeUrl() === d.m ? url : entorno.urlDe(d, location.pathname), false);
      }
      fijarPantalla(pos); notificar();
      return;
    }
    var esRaiz = d.m === raizActual.m && ctxVacio(d.ctx);
    if (esRaiz || pos.i !== 1) {
      // NO reescribe la dirección: los enlaces que leen sus parámetros de
      // la URL (recuperación, «no fui yo») siguen encontrándolos.
      escribir(copia(pos, { tipo: 'modulo', m: d.m, ctx: d.ctx }), url, false);
    } else {
      // Enlace profundo: centinela + raíz + destino. Atrás = Mi trabajo.
      escribir(copia(pos, { tipo: 'modulo', m: raizActual.m, ctx: null }),
               entorno.urlDe({ m: raizActual.m, ctx: null }, location.pathname), false);
      escribir(nueva(2, 'modulo', { m: d.m, ctx: d.ctx }), url, true);
    }
    if (gestoPrevio() === false) rearme = true;
    fijarPantalla(pos); notificar();
  }

  // N2 · REARME AL PRIMER GESTO. Chrome se salta, con el botón atrás, las
  // entradas creadas sin que el usuario haya tocado la página. Si la
  // siembra ocurrió así, se repite UNA vez con el primer gesto: la entrada
  // actual pasa a ser centinela y la raíz (y el destino, si se estaba por
  // encima) se vuelven a empujar, ya con gesto.
  function rearmar() {
    if (!rearme) return;
    if (enVuelo || cola.length) return;                 // en tránsito: se intenta con el siguiente gesto
    rearme = false;
    if (!pos || capasVivas.length) return;
    if (pos.tipo !== 'modulo' && pos.tipo !== 'puerta') return;
    if (pos.i !== 1 && !(pos.i === 2 && raizActual)) return;
    var L = normalizar(history.state);
    if (!L || L.i !== pos.i) return;
    var url = urlActual();
    var actual = copia(L, { y: leerScroll() });
    try {
      history.replaceState(nueva(0, 'centinela'), '', url);
      if (actual.i === 2) {
        history.pushState(nueva(1, 'modulo', { m: raizActual.m, ctx: null }), '',
                          entorno.urlDe({ m: raizActual.m, ctx: null }, location.pathname));
      }
      history.pushState(actual, '', url);
    } catch { /* se queda como estaba */ }
    pos = actual; iNav = actual.i;
  }

  function soltarRaiz() {
    conAviso = false;
    if (!haySesion) apagarAviso();
    if (pantallaActual) { pantallaActual = null; notificar(); }
  }
  function apagarAviso() {
    var estaba = avisoVisible;
    if (avisoVisible) { avisoVisible = false; cancelar(relojAviso); relojAviso = null; }
    avisoEn = -Infinity;
    return estaba;
  }
  // CON SESIÓN, NINGUNA PANTALLA SACA DE TEGO SIN AVISAR · auditoría H2.
  // El aviso de la raíz lo encendía solo `sembrar`, que llama la aplicación
  // al montarse. Las pantallas que se pintan EN LUGAR de la aplicación con
  // la sesión ya iniciada (proteger la cuenta con un segundo factor,
  // verificar la empresa, cambiar la contraseña, confirmar el correo) no
  // siembran, y desde ellas atrás salía al portal al primer toque. Quien
  // lleva la sesión lo dice aquí, y el centinela avisa igual que en la raíz.
  function conSesion(si) {
    haySesion = !!si;
    if (!haySesion && !conAviso && apagarAviso()) notificar();
  }

  function resolverInicial(op) {
    var raiz = typeof op.raiz === 'string' ? op.raiz : op.raiz.m;
    var st = history.state;
    var L = st && st.tego === 2 && st.m && st.tipo !== 'puerta' && st.tipo !== 'centinela' ? st : null;
    var mUrl = moduloDeUrl();
    var m, ctx;
    if (L && (!mUrl || mUrl === L.m)) { m = L.m; ctx = L.ctx; }
    else if (mUrl) { m = mUrl; ctx = op.contextoDeUrl ? op.contextoDeUrl(mUrl, location.search || '') : null; }
    else return { m: raiz, ctx: null };
    // El permiso se comprueba SIEMPRE: un enlace a una pantalla que el rol
    // no tiene cae a la raíz, sin error.
    if (!op.permitido || !op.permitido(m)) return { m: raiz, ctx: null };
    return { m: m, ctx: ctx === undefined ? null : ctx };
  }

  // ---- guardias ------------------------------------------------------
  function ligar(r) {
    // Un registro hecho antes de sembrar se liga a la primera pantalla.
    if (r.mb === null && pos) { r.mb = pos.mb; r.b = pos.b; }
    return r;
  }
  function seAbandona(r, L) {
    ligar(r);
    // Un registro ligado a una subpantalla (`sub`): se abandona en cuanto
    // el aterrizaje ya no la tiene abierta, aunque sea la misma pantalla.
    if (r.sub && valorSub(pos, r.sub) && !valorSub(L, r.sub)) return true;
    return !(L.mb === r.mb && L.i >= r.b);
  }
  function primerCambio(L) {
    // L = aterrizaje (null: se abandona todo, como en `ir`).
    var k;
    for (k = capasVivas.length - 1; k >= 0; k--) {
      if ((L === null || capasVivas[k].i > L.i) && dice(capasVivas[k].tieneCambios)) return capasVivas[k];
    }
    for (k = 0; k < registros.length; k++) {
      if ((L === null || seAbandona(registros[k], L)) && dice(registros[k].tieneCambios)) return registros[k];
    }
    return null;
  }
  function abrirPregunta(origen, alSalir) {
    pendiente = { vista: { detalle: (origen && origen.detalle) || null }, alSalir: alSalir };
    notificar();
  }

  function materializable(L, desde) {
    if (L.tipo === 'modulo' || L.tipo === 'puerta') return true;
    var remonta = !desde || desde.mb !== L.mb;
    if (L.tipo === 'sub') {
      var u = L.subs && L.subs[L.subs.length - 1];
      if (!u) return false;
      if (!remonta && valorSub(desde, u.clave)) return true;       // ya está abierta
      if (u.valor === true) return false;                           // no se puede volver a abrir
      if (remonta) return true;                                     // la pantalla la lee al montarse
      return !!(manejSubs[u.clave] && manejSubs[u.clave].abrir);
    }
    if (L.tipo === 'paso') return !remonta && !!(L.paso && manejPasos[L.paso.clave]);
    if (L.tipo === 'capa') {
      var id = L.capas && L.capas[L.capas.length - 1];
      for (var k = 0; k < capasVivas.length; k++) if (capasVivas[k].id === id) return true;
      return false;
    }
    return false;
  }
  function destinoVivo(L) {
    return (L.tipo === 'capa' || L.tipo === 'paso') ? L.b : L.mb;
  }

  // ---- popstate ------------------------------------------------------
  function alPopstate(ev) {
    var st = ev && ev.state !== undefined ? ev.state : history.state;
    // 0 · una pestaña abierta desde antes del despliegue: su entrada de
    // abajo es la del truco anterior, y significa «portada».
    if (st && st.tego === 'inicio') { salirAlPortal(); return; }
    var L = normalizar(st);
    if (!L) { adoptarAjena(); return; }
    iNav = L.i;

    // 1 · ¿es la llegada de una travesía mía?
    if (enVuelo) {
      var op = enVuelo;
      enVuelo = null;
      cancelar(op.reloj);
      if (L.i === op.destino) {
        if (op.silenciosa) asentarEn(L);
        else aterrizar(L, pos);
        drenar();
        return;
      }
      // El usuario se adelantó: manda él.
    }

    var desde = pos;

    // 2 · había un «¿Salir sin guardar?» abierto y se pulsó atrás otra vez:
    // equivale a «Quedarme».
    if (pendiente) { pendiente = null; notificar(); revertir(desde); return; }

    // 3 · capas vivas por encima del aterrizaje.
    var encima = [];
    for (var k = capasVivas.length - 1; k >= 0; k--) if (capasVivas[k].i > L.i) encima.push(capasVivas[k]);
    if (encima.length && dice(encima[0].bloqueada)) { revertir(desde); return; }

    // 3 y 4 · trabajo sin guardar que este salto abandona.
    var cambio = primerCambio(L);
    if (cambio) {
      revertir(desde);
      var destino = L.i;
      abrirPregunta(cambio, function () { encolar({ hasta: destino }); });
      return;
    }

    aterrizar(L, desde);
  }

  // El navegador ya está en L y nada lo impide: se materializa, o se baja
  // hasta lo que sí se puede mostrar.
  function aterrizar(L, desde) {
    if (L.tipo === 'centinela') { enCentinela(desde); return; }
    if (L.tipo === 'puerta' && conAviso && raizActual) {
      // Con sesión, una entrada de antes de entrar (el acceso) ya no es
      // una pantalla: pasa a ser Mi trabajo.
      L = copia(L, { tipo: 'modulo', m: raizActual.m, ctx: null, subs: [], paso: null, capas: [] });
      try { history.replaceState(L, '', entorno.urlDe({ m: raizActual.m, ctx: null }, location.pathname)); }
      catch { /* nada */ }
    }
    if (!materializable(L, desde)) {
      var vivo = destinoVivo(L);
      if (vivo !== L.i) { encolar({ hasta: vivo }); return; }
    }
    aplicar(L, desde);
  }

  function revertir(desde) { encolar({ hasta: desde.i, silenciosa: true }); }

  // Llegada de una travesía silenciosa (la «X» de una capa, el cierre de
  // una ficha por su flecha, la vuelta tras un aviso): la pantalla ya está
  // como debe; solo se anota dónde quedó el navegador.
  function asentarEn(L) {
    var antes = pos;
    pos = L;
    if (antes && L.b !== antes.b) {
      ys[antes.i] = ultimoY;
      restaurarScroll(scrollGuardado(L));
    }
  }

  function aplicar(L, desde) {
    ys[desde.i] = ultimoY;                       // el scroll de la que se deja
    for (var k = capasVivas.length - 1; k >= 0; k--) {
      if (capasVivas[k].i > L.i) {
        var c = capasVivas[k];
        capasVivas.splice(k, 1);                 // primero se quita: su `cerrarCapa` ya no la encuentra
        try { c.cerrar && c.cerrar(); } catch { /* la capa se cierra sola */ }
      }
    }
    pos = L;
    var remonta = L.mb !== desde.mb || L.m !== desde.m || !mismoCtx(L.ctx, desde.ctx);
    if (remonta) {
      fijarPantalla(L);                          // referencia nueva: quien la mira remonta
    } else {
      var clave;
      for (clave in manejSubs) {
        if (!Object.prototype.hasOwnProperty.call(manejSubs, clave)) continue;
        var antes = valorSub(desde, clave), ahoraSub = valorSub(L, clave);
        try {
          if (!ahoraSub && antes) { manejSubs[clave].cerrar && manejSubs[clave].cerrar(); }
          else if (ahoraSub && ahoraSub.valor !== true && (!antes || antes.valor !== ahoraSub.valor)
                   && manejSubs[clave].abrir) { manejSubs[clave].abrir(ahoraSub.valor); }
        } catch { /* la pantalla decide */ }
      }
      for (clave in manejPasos) {
        if (!Object.prototype.hasOwnProperty.call(manejPasos, clave)) continue;
        var h = manejPasos[clave];
        var nAntes = desde.paso && desde.paso.clave === clave ? desde.paso.n : h.inicial;
        var nAhora = L.paso && L.paso.clave === clave ? L.paso.n : h.inicial;
        if (nAntes !== nAhora) { try { h.fijar(nAhora); } catch { /* la pantalla decide */ } }
      }
    }
    notificar();
    if (L.b !== desde.b) restaurarScroll(scrollGuardado(L));
  }

  // ---- la raíz: aviso y salida --------------------------------------
  function enCentinela(desde) {
    if (!conAviso && !haySesion) { salirAlPortal(); return; }         // sin sesión: sale, como siempre
    if (ahora() - avisoEn <= VENTANA_DE_SALIDA) { salirAlPortal(); return; }   // segundo atrás
    avisoEn = ahora();
    avisoVisible = true;
    cancelar(relojAviso);
    relojAviso = temporizador(function () { relojAviso = null; avisoVisible = false; notificar(); },
                              VENTANA_DE_SALIDA);
    notificar();
    // NO se empuja nada: volver a la raíz es una travesía hacia adelante
    // sobre una entrada que ya existe. La pila queda intacta para el
    // siguiente atrás.
    revertir(desde);
  }
  function salirAlPortal() {
    try { location.replace(entorno.portal()); } catch { /* sin navegador */ }
  }

  // ---- entradas ajenas (anclas `#…`) --------------------------------
  function adoptarAjena() {
    if (!pos) return;
    try {
      var c = copia(pos, { i: pos.i + 1, y: 0 });
      history.replaceState(c, '', urlActual());
      pos = c; iNav = c.i;
    } catch { /* nada */ }
  }
  function ponerseAlDia() {
    // Un clic en un ancla crea una entrada con state nulo y NO dispara
    // `popstate`: se comprueba antes de cada mutación.
    if (!pos || enVuelo || cola.length) return;
    var st = history.state;
    if (st === null || st === undefined) adoptarAjena();
  }
  function antesDeMutar() {
    if (!asegurar()) return false;
    ponerseAlDia();
    rearmar();
    return true;
  }

  // ---- ir ------------------------------------------------------------
  function esLaRaiz(d) { return !!raizActual && d.m === raizActual.m && ctxVacio(d.ctx); }

  function ir(destino, opciones) {
    var reemplazar = !!(opciones && opciones.reemplazar);
    var d = typeof destino === 'string'
      ? { m: destino, ctx: null }
      : { m: destino.m, ctx: destino.ctx === undefined ? null : destino.ctx };
    if (!antesDeMutar()) return false;
    if (pendiente) return false;
    var mismo = pos.tipo === 'modulo' && pos.m === d.m && mismoCtx(pos.ctx, d.ctx);
    if (!(mismo && !capasVivas.length)) {
      var cambio = primerCambio(null);
      if (cambio) {
        abrirPregunta(cambio, function () { irSinGuardia(d, reemplazar); });
        return false;
      }
    }
    irSinGuardia(d, reemplazar);
    return true;
  }

  function irSinGuardia(d, reemplazar) {
    encolar(function () {
      dispararSalidas();
      var empujar = function () {
        if (esLaRaiz(d) && !reemplazar) {
          // Mi trabajo es SIEMPRE la entrada 1: se colapsa, no se apila.
          if (pos.i !== 1) cola.unshift({ hasta: 1 });
          return;
        }
        if (pos.tipo === 'modulo' && pos.m === d.m && mismoCtx(pos.ctx, d.ctx)) return;
        var y = leerScroll();
        try { history.replaceState(copia(pos, { y: y }), '', urlActual()); } catch { /* nada */ }
        delete ys[pos.i];
        var n = reemplazar ? pos.i : pos.i + 1;
        var st = nueva(n, 'modulo', { m: d.m, ctx: d.ctx });
        escribir(st, entorno.urlDe(d, location.pathname), !reemplazar);
        delete ys[n];
        cancelarRestauracion();
        fijarPantalla(st);
        notificar();
        fijarScroll(0);
      };
      if (capasVivas.length) {
        // Elegir un módulo con el menú abierto: primero se cierran las
        // capas (y se consumen sus entradas), después se empuja.
        var vivas = capasVivas;
        capasVivas = [];
        for (var k = vivas.length - 1; k >= 0; k--) {
          try { vivas[k].cerrar && vivas[k].cerrar(); } catch { /* se cierra sola */ }
        }
        cola.unshift(empujar);
        cola.unshift({ hasta: pos.b, silenciosa: true });
      } else {
        empujar();
      }
    });
  }

  function dispararSalidas() {
    var lista = salidas.slice();
    for (var k = 0; k < lista.length; k++) {
      try { lista[k](); } catch { /* un volcado roto no detiene la navegación */ }
    }
  }

  // ---- capas ---------------------------------------------------------
  function abrirCapa(id, opciones) {
    if (!antesDeMutar()) return;
    var o = opciones || {};
    var op = function () {
      op.hecha = true;
      for (var k = 0; k < capasVivas.length; k++) if (capasVivas[k].id === id) return;   // ya está
      var n = pos.i + 1;
      var st = copia(pos, { i: n, tipo: 'capa', capas: (pos.capas || []).concat([id]) });
      delete st.y; delete st.f;
      try { escribir(st, urlActual(), true); } catch { return; }
      capasVivas.push({ id: id, i: n, cerrar: o.cerrar, bloqueada: o.bloqueada,
                        tieneCambios: o.tieneCambios, detalle: o.detalle });
    };
    op.capa = id;
    encolar(op);
  }

  function cerrarCapa(id) {
    // ¿Sigue en la cola, sin empezar? (abrir y cerrar en el mismo turno,
    // como hace StrictMode): se anulan los dos.
    for (var q = 0; q < cola.length; q++) {
      if (typeof cola[q] === 'function' && cola[q].capa === id && !cola[q].hecha) { cola.splice(q, 1); return; }
    }
    var idx = -1;
    for (var k = 0; k < capasVivas.length; k++) if (capasVivas[k].id === id) { idx = k; break; }
    if (idx < 0) return;                 // ya la cerró «atrás»: nada que hacer
    var c = capasVivas[idx];
    capasVivas.splice(idx);              // ella y las de encima, sin llamar a su `cerrar`
    encolar({ hasta: c.i - 1, silenciosa: true });
  }

  // ---- subpantallas --------------------------------------------------
  function registrarSub(clave, manejador) {
    var h = { abrir: manejador && manejador.abrir, cerrar: manejador && manejador.cerrar };
    manejSubs[clave] = h;
    return function () { if (manejSubs[clave] === h) delete manejSubs[clave]; };
  }
  function urlConParametro(clave, valor) {
    var q = new URLSearchParams(location.search || '');
    if (valor === null || valor === undefined) q.delete(clave); else q.set(clave, String(valor));
    var cadena = q.toString();
    return location.pathname + (cadena ? '?' + cadena : '');
  }

  function abrirSub(clave, opciones) {
    if (!antesDeMutar()) return;
    var o = opciones || {};
    if (o.cerrar || o.abrir) registrarSub(clave, o);
    var valor = o.valor === undefined ? true : (serializable(o.valor) ? o.valor : true);
    encolar(function () {
      var ya = valorSub(pos, clave);
      var enUrl = !!o.enUrl && valor !== true;
      if (ya) {
        // La misma clave no se apila dos veces: cambiar de ficha o de
        // pestaña sustituye la entrada.
        if (ya.valor === valor) return;
        var subs = [];
        for (var k = 0; k < pos.subs.length; k++) {
          subs.push(pos.subs[k].clave === clave ? { clave: clave, valor: valor, i: pos.subs[k].i } : pos.subs[k]);
        }
        try { escribir(copia(pos, { subs: subs }), enUrl ? urlConParametro(clave, valor) : urlActual(), false); }
        catch { /* nada */ }
        return;
      }
      dispararSalidas();
      var y = scrollQueSeDeja();
      try { history.replaceState(copia(pos, { y: y }), '', urlActual()); } catch { /* nada */ }
      delete ys[pos.i];
      var n = pos.i + 1;
      var st = copia(pos, { i: n, tipo: 'sub', b: n, paso: null, y: 0,
                            subs: (pos.subs || []).concat([{ clave: clave, valor: valor, i: n }]) });
      delete st.f;
      try { escribir(st, enUrl ? urlConParametro(clave, valor) : urlActual(), true); } catch { return; }
      delete ys[n];
      // Una pestaña (`reemplaza`) no mueve la vista; una ficha empieza arriba.
      if (!o.reemplaza) { cancelarRestauracion(); fijarScroll(0); }
    });
  }

  function cerrarSub(clave) {
    if (!pos) return;
    encolar(function () {
      var c = valorSub(pos, clave);
      if (!c) return;                    // ya la cerró «atrás»
      // Lo que hubiera encima (capas de esa ficha) se va con ella.
      for (var k = capasVivas.length - 1; k >= 0; k--) if (capasVivas[k].i >= c.i) capasVivas.splice(k, 1);
      cola.unshift({ hasta: c.i - 1, silenciosa: true });
    });
  }

  // ---- pasos ---------------------------------------------------------
  function registrarPasos(clave, manejador) {
    var h = { fijar: manejador.fijar, inicial: manejador.inicial === undefined ? 0 : manejador.inicial };
    manejPasos[clave] = h;
    return function () { if (manejPasos[clave] === h) delete manejPasos[clave]; };
  }

  function paso(n, opciones) {
    var clave = (opciones && opciones.clave) || 'paso';
    if (!antesDeMutar()) return;
    encolar(function () {
      var h = manejPasos[clave];
      var actual = pos.paso && pos.paso.clave === clave ? pos.paso : null;
      var nActual = actual ? actual.n : (h ? h.inicial : 0);
      if (n === nActual) return;
      if (n > nActual) {
        var i = pos.i + 1;
        var st = copia(pos, { i: i, tipo: 'paso',
                              paso: { clave: clave, n: n, i0: actual ? actual.i0 : pos.i,
                                      ns: (actual ? actual.ns : []).concat([n]) } });
        delete st.y; delete st.f;
        try { escribir(st, urlActual(), true); } catch { /* nada */ }
        return;
      }
      // Bajar por código (el botón «Paso anterior»): se consumen las
      // entradas sobrantes, hasta la del paso más alto que no pase de n.
      if (!actual) return;
      var hasta = actual.i0;
      for (var k = 0; k < actual.ns.length; k++) if (actual.ns[k] <= n) hasta = actual.i0 + 1 + k;
      for (var c = capasVivas.length - 1; c >= 0; c--) if (capasVivas[c].i > hasta) capasVivas.splice(c, 1);
      cola.unshift({ hasta: hasta, silenciosa: true });
    });
  }

  // ---- atrás, guardia y lo demás ------------------------------------
  function atras() {
    if (pendiente) { responder(false); return; }
    if (!asegurar()) return;
    history.go(-1);                      // como el botón del teléfono, con sus guardias
  }

  function conGuardia(fn, opciones) {
    var cambio = primerCambio(null);
    if (!cambio) { fn(); return true; }
    abrirPregunta(opciones && opciones.detalle ? { detalle: opciones.detalle } : cambio, fn);
    return false;
  }

  function registrarCambios(tieneCambios, opciones) {
    var r = { mb: pos ? pos.mb : null, b: pos ? pos.b : null, tieneCambios: tieneCambios,
              detalle: (opciones && opciones.detalle) || null,
              sub: (opciones && opciones.sub) || null };
    registros.push(r);
    return function () {
      var k = registros.indexOf(r);
      if (k >= 0) registros.splice(k, 1);
    };
  }

  function responder(salir) {
    var p = pendiente;
    if (!p) return;
    pendiente = null;
    notificar();
    if (salir) p.alSalir();
  }

  function reescribirUrl(url, opciones) {
    var st = history.state;
    var siguiente = st === undefined ? null : st;
    if (opciones && opciones.filtros !== undefined && normalizar(st)) {
      siguiente = copia(st, { f: opciones.filtros });
    }
    history.replaceState(siguiente, '', url);
    if (pos && siguiente && siguiente.tego === 2 && siguiente.s === s && siguiente.i === pos.i) pos = siguiente;
  }

  function reescribirContexto(ctx) {
    if (!pos || !pos.m) return;
    var c = ctxVacio(ctx) ? null : ctx;
    try { escribir(copia(pos, { ctx: c }), entorno.urlDe({ m: pos.m, ctx: c }, location.pathname), false); }
    catch { /* nada */ }
  }

  function antesDeSalir(fn) {
    salidas.push(fn);
    return function () {
      var k = salidas.indexOf(fn);
      if (k >= 0) salidas.splice(k, 1);
    };
  }

  function suscribir(fn) {
    suscriptores.push(fn);
    return function () {
      var k = suscriptores.indexOf(fn);
      if (k >= 0) suscriptores.splice(k, 1);
    };
  }

  return {
    iniciar: iniciar,
    sembrar: sembrar,
    soltarRaiz: soltarRaiz,
    conSesion: conSesion,
    resolverInicial: resolverInicial,
    ir: ir,
    abrirCapa: abrirCapa,
    cerrarCapa: cerrarCapa,
    abrirSub: abrirSub,
    cerrarSub: cerrarSub,
    registrarSub: registrarSub,
    registrarPasos: registrarPasos,
    paso: paso,
    atras: atras,
    conGuardia: conGuardia,
    enRaiz: function () { return !!pos && pos.i === 1 && capasVivas.length === 0; },
    pantalla: function () { return pantallaActual; },
    suscribir: suscribir,
    registrarCambios: registrarCambios,
    confirmacion: function () { return pendiente ? pendiente.vista : null; },
    responder: responder,
    avisoDeSalida: function () { return avisoVisible; },
    reescribirUrl: reescribirUrl,
    reescribirContexto: reescribirContexto,
    antesDeSalir: antesDeSalir,
    idEntrada: function () { return pos ? pos.s + ':' + pos.i : null; },
    estadoDeEntrada: function () {
      var L = normalizar(history.state);
      return L ? copia(L, {}) : null;
    },
    salirAlPortal: salirAlPortal
  };
}
/* NUCLEO:FIN */

export { crearNavegacion };

// ---------------------------------------------------------------------
// EL CABLEADO AL NAVEGADOR DE VERDAD
// ---------------------------------------------------------------------

// Las aplicaciones hermanas (tienda pública, terminal, panel, reportar por
// QR) son públicas o de aparato: en su raíz, atrás sale como siempre. Es
// la misma bifurcación por ruta de `main.jsx`.
const SIN_CENTINELA = ['/reportar', '/terminal-virtual', '/tienda', '/panel'];

/** El entorno del núcleo para una ventana de verdad (o un doble de pruebas). */
export function entornoDe(win) {
  const doc = win.document;
  return {
    history: win.history,
    location: win.location,
    escuchar: (tipo, fn, opciones) => win.addEventListener(tipo, fn, opciones),
    ahora: () => Date.now(),
    temporizador: (fn, ms) => {
      const t = setTimeout(fn, ms);
      // En Node (las pruebas) el reloj no debe retener el proceso.
      if (t && typeof t.unref === 'function') t.unref();
      return t;
    },
    cancelar: (t) => clearTimeout(t),
    leerScroll: () => win.scrollY || 0,
    fijarScroll: (y) => { if (typeof win.scrollTo === 'function') win.scrollTo(0, y); },
    topeDeScroll: () => (doc ? doc.documentElement.scrollHeight - win.innerHeight : 0),
    urlDe,
    portal: () => `${win.location.protocol}//${win.location.host}/`,
    gestoPrevio: () => win.navigator?.userActivation?.hasBeenActive,
    centinelaPorOmision: () => !SIN_CENTINELA.some((r) => (win.location.pathname || '').startsWith(r)),
  };
}

/**
 * Cambia la dirección SIN borrar el state de la entrada. Para las dos
 * traducciones de enlace que corren antes de sembrar (`/q/…`, `?ir=…`):
 * con state nulo, nulo se queda.
 */
export function reescribirUrlEn(win, url) {
  win.history.replaceState(win.history.state ?? null, '', url);
}

// La instancia única, perezosa: se crea al primer uso, no al importar.
let instancia = null;
function laInstancia() {
  if (!instancia) instancia = crearNavegacion(entornoDe(window));
  return instancia;
}

const METODOS = [
  'iniciar', 'sembrar', 'soltarRaiz', 'conSesion', 'resolverInicial', 'ir', 'abrirCapa', 'cerrarCapa',
  'abrirSub', 'cerrarSub', 'registrarSub', 'registrarPasos', 'paso', 'atras', 'conGuardia',
  'enRaiz', 'pantalla', 'suscribir', 'registrarCambios', 'confirmacion', 'responder',
  'avisoDeSalida', 'reescribirUrl', 'reescribirContexto', 'antesDeSalir', 'idEntrada',
  'estadoDeEntrada', 'salirAlPortal',
];

// Cada método es una función estable (sirve tal cual a `useSyncExternalStore`).
export const navegacion = Object.fromEntries(
  METODOS.map((nombre) => [nombre, (...args) => laInstancia()[nombre](...args)]),
);
